import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { normalizeNavPrefs, type NavGroup, type UserNavPrefs } from "@shared/nav";

// 自分の画面のサイドメニュー表示・色。Layout が持ち、設定画面・表示設定の窓から変更する（変更はすぐメニューに反映）。
// サーバ（ユーザーごと）に保存するので、PCとスマホで同じ表示になる。端末にも控えて、開いた瞬間の色ちらつきを防ぐ。
export type SaveStatus = "" | "saving" | "saved" | "error";
export interface NavPrefsCtl {
  prefs: UserNavPrefs;
  available: NavGroup[]; // 権限・組織の設定を反映した、表示を選べる項目
  update: (fn: (p: UserNavPrefs) => UserNavPrefs, opts?: { debounce?: boolean }) => void;
  status: SaveStatus;
  error: string;
}

const Ctx = createContext<NavPrefsCtl | null>(null);
export const NavPrefsProvider = Ctx.Provider;
export function useNavPrefs(): NavPrefsCtl | null { return useContext(Ctx); }

// 端末の控え。dirty＝まだサーバに届いていない変更（閉じる直前の変更など）。次に開いたときはこちらを優先して送り直す。
const cacheKey = (userId: string) => `nav_prefs_v1:${userId}`;
const readCache = (userId: string | undefined): { prefs: UserNavPrefs; dirty: boolean } => {
  if (!userId) return { prefs: {}, dirty: false };
  try {
    const raw = JSON.parse(localStorage.getItem(cacheKey(userId)) || "null");
    if (raw && typeof raw === "object" && "prefs" in raw) return { prefs: normalizeNavPrefs(raw.prefs), dirty: raw.dirty === true };
    return { prefs: normalizeNavPrefs(raw), dirty: false };
  } catch { return { prefs: {}, dirty: false }; }
};
const writeCache = (userId: string | undefined, prefs: UserNavPrefs, dirty: boolean) => {
  if (!userId) return;
  try { localStorage.setItem(cacheKey(userId), JSON.stringify({ prefs, dirty })); } catch { /* 保存できない端末は無視 */ }
};

export function useNavPrefsState(userId: string | undefined): Omit<NavPrefsCtl, "available"> {
  const [prefs, setPrefs] = useState<UserNavPrefs>(() => readCache(userId).prefs);
  const [status, setStatus] = useState<SaveStatus>("");
  const [error, setError] = useState("");
  const pending = useRef<UserNavPrefs | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    setStatus("saving");
    api.put("/nav-prefs", { prefs: p })
      .then(() => { setStatus("saved"); setError(""); if (!pending.current) writeCache(userId, p, false); })
      .catch((e) => { setStatus("error"); setError(e instanceof Error ? e.message : "保存できませんでした"); });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const cached = readCache(userId);
    setPrefs(cached.prefs);
    if (cached.dirty) { pending.current = cached.prefs; flush(); return; } // 前回届かなかった変更を送り直す
    api.get<{ prefs: UserNavPrefs }>("/nav-prefs")
      .then((d) => { if (pending.current) return; const p = normalizeNavPrefs(d.prefs); setPrefs(p); writeCache(userId, p, false); })
      .catch(() => { /* 読めなくても既定の表示で使える */ });
  }, [userId, flush]);

  // 並び替え・折りたたみ・表示切替はすぐ保存。色を動かしている間だけは、操作が止まってから保存する。
  const update = useCallback((fn: (p: UserNavPrefs) => UserNavPrefs, opts?: { debounce?: boolean }) => {
    setPrefs((prev) => {
      const next = normalizeNavPrefs(fn(prev));
      pending.current = next;
      writeCache(userId, next, true);
      return next;
    });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, opts?.debounce ? 500 : 0);
  }, [flush, userId]);

  // 画面を閉じる・再読み込みする直前に、まだ送っていない変更を送る（keepalive で閉じても届く）
  useEffect(() => {
    const onHide = () => {
      const p = pending.current;
      if (!p) return;
      try {
        fetch("/api/nav-prefs", { method: "PUT", credentials: "same-origin", keepalive: true, headers: { "Content-Type": "application/json" }, body: JSON.stringify({ prefs: p }) })
          .then((r) => { if (r.ok) writeCache(userId, p, false); }).catch(() => { /* 次に開いたとき送り直す */ });
      } catch { /* 非対応 */ }
    };
    window.addEventListener("pagehide", onHide);
    return () => { window.removeEventListener("pagehide", onHide); if (timer.current) clearTimeout(timer.current); flush(); };
  }, [flush, userId]);

  return { prefs, update, status, error };
}
